// AdminDashboard: manage tours, users, bookings, reviews
import React, { useState, useEffect } from "react";
import tourApi from "../api/tour";
import authApi from "../api/auth";
import reviewApi from "../api/review";
import adminApi from "../api/admin";
import AdminOverview from "../components/AdminOverview";
import TourForm from "../components/TourForm";
import TourList from "../components/TourList";
import bookingApi from "../api/booking";
import AlertMessage from "../components/AlertMessage";
import contactApi from "../api/contact";
import ExportButton from "../components/ExportButton";
import RevenueOverview from "../components/RevenueOverview";
import { defaultHeroImages } from "../components/HeroSection";
import settingsApi from "../api/settings";
// import { useAuth } from "../context/AuthContext";

export default function AdminDashboard({ section = "overview" }) {
  // console.log("🎯 AdminDashboard - Rendu avec section:", section);
  // const { user } = useAuth();
  // console.log("🎯 AdminDashboard - User:", user);

  const [stats, setStats] = useState({
    tours: 0,
    users: 0,
    bookings: 0,
    reviews: 0,
  });

  const [bookingsData, setBookingsData] = useState([]);
  const [trends, setTrends] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState("");

  useEffect(() => {
    if (section === "overview") {
      loadDashboardStats();
    }
  }, [section]);

  const loadDashboardStats = async () => {
    setIsLoadingStats(true);
    setStatsError("");
    try {
      // Try to get comprehensive stats from admin API
      try {
        const { data } = await adminApi.getDashboardStats();
        setStats({
          tours: data.data.total_tours || 0,
          users: data.data.total_users || 0,
          bookings: data.data.total_bookings || 0,
          reviews: data.data.total_reviews || 0,
        });

        if (data.data.trends) {
          setTrends(data.data.trends);
        }

        // Also load bookings data for charts
        const bookingsResponse = await bookingApi.listBookings();
        setBookingsData(bookingsResponse.data || []);
      } catch (adminErr) {
        // Fallback to individual API calls

        // Fallback: get individual stats
        const [toursRes, usersRes, bookingsRes, reviewsRes] =
          await Promise.allSettled([
            tourApi.listTours(),
            authApi.getUserStats(),
            bookingApi.listBookings(),
            reviewApi.getReviewStats(),
          ]);

        setStats({
          tours:
            toursRes.status === "fulfilled" ? toursRes.value.data.length : 0,
          users:
            usersRes.status === "fulfilled"
              ? usersRes.value.data.total || usersRes.value.data.length || 0
              : 0,
          bookings:
            bookingsRes.status === "fulfilled"
              ? bookingsRes.value.data.length
              : 0,
          reviews:
            reviewsRes.status === "fulfilled"
              ? reviewsRes.value.data.total || reviewsRes.value.data.length || 0
              : 0,
        });

        // Set bookings data from fallback
        if (bookingsRes.status === "fulfilled") {
          setBookingsData(bookingsRes.value.data || []);
        }
      }
    } catch (err) {
      setStatsError(
        `Error loading statistics: ${err.message}`
      );
    } finally {
      setIsLoadingStats(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Admin - {section}</h1>
      {section === "overview" && (
        <>
          {/* Quick Export Actions */}
          <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-800 p-6 bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-4">
              <span className="text-2xl">⚡</span>
              <span>Quick Actions</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-700 ml-2">
                📄 CSV Export
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ExportButton
                endpoint="/admin/export/revenue"
                filename={`revenue-report-${new Date().toISOString().split('T')[0]}.csv`}
                label="Export Revenue Report"
                icon="💰"
              />
              <ExportButton
                endpoint="/admin/export/tours"
                filename={`tours-export-${new Date().toISOString().split('T')[0]}.csv`}
                label="Export Tours"
                icon="🎫"
              />
              <ExportButton
                endpoint="/admin/export/bookings"
                filename={`bookings-export-${new Date().toISOString().split('T')[0]}.csv`}
                label="Export Bookings"
                icon="📊"
              />
            </div>
          </div>

          {/* Revenue Dashboard */}
          <RevenueOverview />

          <AdminOverview
            stats={[
              { n: stats.tours, label: "Tours" },
              { n: stats.users, label: "Users" },
              { n: stats.bookings, label: "Bookings" },
              { n: stats.reviews, label: "Reviews" },
            ]}
            isLoading={isLoadingStats}
            error={statsError}
            onRetry={loadDashboardStats}
            bookingsData={bookingsData}
            trends={trends}
          />
        </>
      )}
      {section === "tours" && <AdminTours />}
      {section === "users" && <AdminUsers />}
      {section === "bookings" && <AdminBookingsComponent />}
      {section === "reviews" && <AdminReviews />}
      {section === "messages" && <AdminContactMessages />}
      {section === "settings" && <AdminSettings />}
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const USERS_PER_PAGE = 10;

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await authApi.listUsers();
      setUsers(data.data);
      setUsersPage(1);
      // console.log("API /admin/users data:", data);
    } catch (err) {
      // Error handled by setError below

      // Try to get more specific error information
      if (err.response?.status === 401) {
        setError("Authentification requise. Veuillez vous reconnecter.");
      } else if (err.response?.status === 403) {
        setError("Accès refusé. Droits administrateur requis.");
      } else if (err.response?.status === 404) {
        setError(
          "Endpoint /admin/users non trouvé. Vérifiez la configuration backend."
        );
      } else {
        setError(
          `Erreur ${err.response?.status || "inconnue"}: ${
            err.response?.data?.message || err.message
          }`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?\n\nTheir bookings will be anonymized but preserved.`)) {
      return;
    }

    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      await authApi.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      setSuccess(`User "${userName}" deleted successfully. Their bookings have been anonymized.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 w-32 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
        <div className="h-7 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="flex gap-16">
            {[1,2,3,4].map(i => <div key={i} className="h-4 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />)}
          </div>
        </div>
        {[1,2,3,4,5].map(i => (
          <div key={i} className="flex items-center gap-6 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
            <div className="h-5 w-10 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
              <div className="h-4 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="h-4 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-4 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
          </div>
        ))}
      </div>
    </div>
  );

  const filteredUsers = usersSearch.trim()
    ? users.filter((u) => {
        const q = usersSearch.toLowerCase();
        return (
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
        );
      })
    : users;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Users</h1>
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
          {users.length} {users.length === 1 ? 'User' : 'Users'}
        </span>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={usersSearch}
          onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(1); }}
          className="w-full rounded-lg border border-slate-300 pl-10 pr-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
        <svg className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {error && <AlertMessage type="error">{error}</AlertMessage>}
      {success && <AlertMessage type="success">{success}</AlertMessage>}

      {filteredUsers.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
            <span className="text-3xl">👥</span>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {usersSearch.trim() ? "No users match your search" : "No users yet"}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {usersSearch.trim() ? "Try a different search term" : "Registered users will appear here"}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    Registered
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {filteredUsers.slice((usersPage - 1) * USERS_PER_PAGE, usersPage * USERS_PER_PAGE).map((user) => (
                  <tr
                    key={user.id}
                    className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-300">
                        #{user.id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {user.name[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">
                            {user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`mailto:${user.email}`}
                        className="text-sm text-emerald-600 hover:underline dark:text-emerald-400"
                      >
                        {user.email}
                      </a>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                      {new Date(user.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        disabled={isLoading}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                        title="Delete user"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users Pagination */}
      {filteredUsers.length > USERS_PER_PAGE && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
            disabled={usersPage === 1}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Previous
          </button>
          {Array.from({ length: Math.ceil(filteredUsers.length / USERS_PER_PAGE) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setUsersPage(i + 1)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                usersPage === i + 1
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setUsersPage((p) => Math.min(Math.ceil(filteredUsers.length / USERS_PER_PAGE), p + 1))}
            disabled={usersPage === Math.ceil(filteredUsers.length / USERS_PER_PAGE)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
function AdminContactMessages() {
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesSearch, setMessagesSearch] = useState("");
  const MESSAGES_PER_PAGE = 10;

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await contactApi.listContactMessages();
      setMessages(data.data);
      setMessagesPage(1);
    } catch (err) {
      // Error handled by setError
      if (err.response?.status === 401) {
        setError("Authentification requise. Veuillez vous reconnecter.");
      } else if (err.response?.status === 403) {
        setError("Accès refusé. Droits administrateur requis.");
      } else {
        setError(
          `Erreur ${err.response?.status || "inconnue"}: ${
            err.response?.data?.message || err.message
          }`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce message ?")) return;
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      await contactApi.deleteContactMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setSuccess("Message supprimé avec succès !");
    } catch (err) {
      setError(
        err.response?.data?.message || "Échec de la suppression du message."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleRead = async (id) => {
    try {
      const { data } = await contactApi.toggleReadMessage(id);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_read: data.data.is_read } : m))
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update message status.");
    }
  };

  if (isLoading && messages.length === 0)
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-7 w-28 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="h-10 w-full animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
        {[1,2,3].map(i => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-32 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-5 w-10 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
                </div>
                <div className="h-4 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="h-4 w-48 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            </div>
          </div>
        ))}
      </div>
    );

  const unreadCount = messages.filter((m) => !m.is_read).length;

  const filteredMessages = messagesSearch.trim()
    ? messages.filter((m) => {
        const q = messagesSearch.toLowerCase();
        return (
          (m.name || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q) ||
          (m.subject || "").toLowerCase().includes(q)
        );
      })
    : messages;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Contact Messages</h1>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
              {unreadCount} Unread
            </span>
          )}
          <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            {messages.length} {messages.length === 1 ? 'Message' : 'Messages'}
          </span>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search by name, email, or subject..."
          value={messagesSearch}
          onChange={(e) => { setMessagesSearch(e.target.value); setMessagesPage(1); }}
          className="w-full rounded-lg border border-slate-300 pl-10 pr-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
        <svg className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {error && <AlertMessage type="error">{error}</AlertMessage>}
      {success && <AlertMessage type="success">{success}</AlertMessage>}

      {filteredMessages.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
            <span className="text-3xl">📩</span>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {messagesSearch.trim() ? "No messages match your search" : "No messages yet"}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {messagesSearch.trim() ? "Try a different search term" : "Contact messages will appear here"}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredMessages.slice((messagesPage - 1) * MESSAGES_PER_PAGE, messagesPage * MESSAGES_PER_PAGE).map((msg) => (
            <div
              key={msg.id}
              className={`group rounded-xl border p-6 shadow-sm transition-all hover:shadow-md dark:bg-slate-900 ${
                msg.is_read
                  ? "border-slate-200 bg-white dark:border-slate-800"
                  : "border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {!msg.is_read && (
                          <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                        <h3 className={`text-slate-900 dark:text-slate-100 ${msg.is_read ? "font-semibold" : "font-bold"}`}>
                          {msg.name}
                        </h3>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          #{msg.id}
                        </span>
                      </div>
                      <a
                        href={`mailto:${msg.email}`}
                        className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline dark:text-emerald-400"
                      >
                        <span>📧</span>
                        {msg.email}
                      </a>
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(msg.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>

                  {/* Subject */}
                  {msg.subject && (
                    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Subject: {msg.subject}
                      </p>
                    </div>
                  )}

                  {/* Message */}
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                      {msg.message || <span className="italic text-slate-400">No message</span>}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 opacity-0 transition-all group-hover:opacity-100">
                  <button
                    onClick={() => handleToggleRead(msg.id)}
                    className={`rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors ${
                      msg.is_read
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                    title={msg.is_read ? "Mark as unread" : "Mark as read"}
                  >
                    {msg.is_read ? "Unread" : "Read"}
                  </button>
                  <button
                    onClick={() => handleDelete(msg.id)}
                    className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                    title="Delete message"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Messages Pagination */}
      {filteredMessages.length > MESSAGES_PER_PAGE && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setMessagesPage((p) => Math.max(1, p - 1))}
            disabled={messagesPage === 1}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Previous
          </button>
          {Array.from({ length: Math.ceil(filteredMessages.length / MESSAGES_PER_PAGE) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setMessagesPage(i + 1)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                messagesPage === i + 1
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setMessagesPage((p) => Math.min(Math.ceil(filteredMessages.length / MESSAGES_PER_PAGE), p + 1))}
            disabled={messagesPage === Math.ceil(filteredMessages.length / MESSAGES_PER_PAGE)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

const REVIEWS_PER_PAGE = 10;

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsSearch, setReviewsSearch] = useState("");

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await reviewApi.listAllReviews();
      setReviews(data.data);
    } catch (err) {
      // Error handled by setError

      // Try to get more specific error information
      if (err.response?.status === 401) {
        setError("Authentification requise. Veuillez vous reconnecter.");
      } else if (err.response?.status === 403) {
        setError("Accès refusé. Droits administrateur requis.");
      } else if (err.response?.status === 404) {
        setError(
          "Endpoint /admin/reviews non trouvé. Vérifiez la configuration backend."
        );
      } else {
        setError(
          `Erreur ${err.response?.status || "inconnue"}: ${
            err.response?.data?.message || err.message
          }`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cet avis ?")) return;
    setError("");
    setIsLoading(true);
    try {
      await reviewApi.deleteReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(
        err.response?.data?.message || "Échec de la suppression de l'avis."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 w-32 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
        <div className="h-7 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
      </div>
      {[1,2,3].map(i => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="h-5 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="h-5 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="h-4 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
          </div>
        </div>
      ))}
    </div>
  );

  const filteredReviews = reviews.filter((r) => {
    if (!reviewsSearch.trim()) return true;
    const q = reviewsSearch.toLowerCase();
    return (
      (r.user?.name || "").toLowerCase().includes(q) ||
      (r.tour?.title || "").toLowerCase().includes(q) ||
      (r.comment || "").toLowerCase().includes(q)
    );
  });

  const reviewsTotalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);
  const paginatedReviews = filteredReviews.slice(
    (reviewsPage - 1) * REVIEWS_PER_PAGE,
    reviewsPage * REVIEWS_PER_PAGE
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Reviews</h1>
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
          {filteredReviews.length} {filteredReviews.length === 1 ? 'Review' : 'Reviews'}
        </span>
      </div>

      {/* Search Bar */}
      {reviews.length > 0 && (
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            placeholder="Search reviews by user, tour, or content..."
            value={reviewsSearch}
            onChange={(e) => { setReviewsSearch(e.target.value); setReviewsPage(1); }}
            className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-4 text-sm transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
      )}

      {error && <AlertMessage type="error">{error}</AlertMessage>}

      {filteredReviews.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
            <span className="text-3xl">⭐</span>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {reviewsSearch ? 'No reviews match your search' : 'No reviews yet'}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {reviewsSearch ? 'Try a different search term' : 'Customer reviews will appear here'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {paginatedReviews.map((review) => (
            <div
              key={review.id}
              className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            >
              {/* Header with User Info and Rating */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      {(review.user?.name || 'A')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {review.user?.name || 'Anonymous'}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-lg leading-none text-yellow-500">
                          {Array(review.rating).fill("★").join("")}
                          {Array(5 - review.rating).fill("☆").join("")}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {review.rating}/5
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(review.id)}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-all hover:bg-red-700 group-hover:opacity-100"
                  title="Delete review"
                >
                  Delete
                </button>
              </div>

              {/* Tour Info */}
              <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">Tour</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">
                  {review.tour?.title || 'Unknown Tour'}
                </p>
              </div>

              {/* Comment */}
              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  "{review.comment}"
                </p>
              </div>

              {/* Date */}
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <span>📅</span>
                <span>
                  {new Date(review.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredReviews.length > REVIEWS_PER_PAGE && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
            disabled={reviewsPage === 1}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Previous
          </button>
          {Array.from({ length: reviewsTotalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setReviewsPage(i + 1)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                reviewsPage === i + 1
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setReviewsPage((p) => Math.min(reviewsTotalPages, p + 1))}
            disabled={reviewsPage === reviewsTotalPages}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function AdminTours() {
  const [tours, setTours] = React.useState([]);
  const [editingTourId, setEditingTourId] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [formData, setFormData] = React.useState({
    title: "",
    city: "",
    duration_days: "",
    start_date: "",
    price: "",
    rating: "",
    thumbnail: null,
    images: [],
    summary: "",
    itinerary: [], // ✅ Array vide au lieu de string vide
  });
  const [thumbnailPreview, setThumbnailPreview] = React.useState(null);
  const [imagePreviews, setImagePreviews] = React.useState([]);
  const [imagesToDelete, setImagesToDelete] = React.useState([]); // ✅ Tracker les images à supprimer
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setError("");
      setIsLoading(true);
      try {
        const { data } = await tourApi.listTours();
        if (active) setTours(data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Échec du chargement des circuits."
        );
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // const handleInputChange = (field, value) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     [field]: value,
  //   }));
  // };
  const handleInputChange = (field, value) => {
    // ✅ Ne pas formater le prix pendant la saisie, laisser l'utilisateur taper librement
    setFormData({ ...formData, [field]: value });
  };
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        thumbnail: file,
      }));
      const reader = new FileReader();
      reader.onload = (e) => setThumbnailPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    // ✅ Ajouter les nouveaux fichiers au lieu de remplacer
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreviews((prev) => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const imageToRemove = imagePreviews[index];

    // ✅ Si c'est une URL (ancienne image du serveur), l'ajouter à la liste de suppression
    if (typeof imageToRemove === 'string' && imageToRemove.startsWith('http')) {
      // Extraire le chemin relatif depuis l'URL (ex: "thumbnails/xxx.jpg")
      const urlParts = imageToRemove.split('/storage/');
      if (urlParts.length > 1) {
        const relativePath = urlParts[1];
        setImagesToDelete((prev) => [...prev, relativePath]);
      }
    }

    // Supprimer de l'aperçu et du formData
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const add = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);
    const requiredFields = [
      "title",
      "city",
      "duration_days",
      "price",
      "rating",
      "thumbnail",
      "summary",
    ];
    const missingFields = requiredFields.filter((field) => {
      if (field === "thumbnail") {
        return !formData[field];
      }
      return !formData[field].toString().trim();
    });

    if (missingFields.length > 0) {
      setError(
        `Veuillez remplir tous les champs obligatoires: ${missingFields.join(
          ", "
        )}`
      );
      setIsLoading(false);
      return;
    }

    // const data = new FormData();
    // for (const key in formData) {
    //   if (key === "images") {
    //     formData.images.forEach((image) => data.append("images[]", image));
    //   } else if (key === "thumbnail") {
    //     data.append("thumbnail", formData.thumbnail);
    //   } else if (key === "itinerary") {
    //     const itineraryArray = formData.itinerary
    //       .split(",")
    //       .map((item, index) => {
    //         const [dayStr, ...descParts] = item.split(":");
    //         return {
    //           day: index + 1, // converti en entier
    //           description: descParts.join(":").trim() || dayStr.trim(),
    //         };
    //       });

    //     data.append("itinerary", JSON.stringify(itineraryArray));
    //   } else {
    //     data.append(key, formData[key]);
    //   }
    // }
    // ✅ Préparez les données correctement
    const tourData = {
      title: formData.title,
      city: formData.city,
      duration_days: Number(formData.duration_days),
      start_date: formData.start_date || null,
      price: formData.price,
      rating: Number(formData.rating),
      summary: formData.summary,
      thumbnail: formData.thumbnail, // File object
      images: formData.images, // Array of File objects
    };

    // ✅ Ajoutez itinerary seulement si rempli (format JSON array)
    if (Array.isArray(formData.itinerary) && formData.itinerary.length > 0) {
      tourData.itinerary = formData.itinerary;
    }

    try {
      const { data: newTour } = await tourApi.createTour(tourData);
      setTours((t) => [...t, newTour]);
      setSuccess("Circuit ajouté avec succès !");
    } catch (err) {
      // Error handled by setError
      setError(err.response?.data?.message || "Échec de l'ajout du circuit.");
    } finally {
      setIsLoading(false);
    }
    setFormData({
      title: "",
      city: "",
      duration_days: "",
      start_date: "",
      price: "",
      rating: "",
      thumbnail: null,
      images: [],
      summary: "",
      itinerary: [], // ✅ Array vide
    });
    setThumbnailPreview(null);
    setImagePreviews([]);
    setShowForm(false); // Hide the form after adding
  };

  const remove = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce circuit ?")) return;
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      await tourApi.deleteTour(id);
      setTours((t) => t.filter((tour) => tour.id !== id));
      setSuccess("Circuit supprimé avec succès !");
    } catch (err) {
      setError(
        err.response?.data?.message || "Échec de la suppression du circuit."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (tour) => {
    setEditingTourId(tour.id);

    // ✅ Parser l'itinerary si c'est une chaîne JSON
    let parsedItinerary = [];
    if (tour.itinerary) {
      if (typeof tour.itinerary === 'string') {
        try {
          parsedItinerary = JSON.parse(tour.itinerary);
        } catch (e) {
          // Itinerary parse failed, use raw value
          parsedItinerary = [];
        }
      } else if (Array.isArray(tour.itinerary)) {
        parsedItinerary = tour.itinerary;
      }
    }

    setFormData({
      title: tour.title,
      city: tour.city,
      duration_days: tour.duration_days,
      start_date: tour.start_date ? tour.start_date.split('T')[0] : "",
      price: tour.price,
      rating: tour.rating,
      thumbnail: tour.thumbnail,
      images: [], // ✅ Tableau vide, les anciennes images sont déjà sur le serveur
      summary: tour.summary,
      itinerary: parsedItinerary, // ✅ Array JSON parsé ou vide
    });

    setThumbnailPreview(tour.thumbnail);
    setImagePreviews(tour.images);
    setImagesToDelete([]); // ✅ Réinitialiser la liste des images à supprimer
    setShowForm(true);
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);
    const requiredFields = [
      "title",
      "city",
      "duration_days",
      "price",
      "rating",
      "thumbnail",
      "summary",
    ];
    const missingFields = requiredFields.filter((field) => {
      if (field === "thumbnail") {
        return !formData[field];
      }
      return !formData[field].toString().trim();
    });

    if (missingFields.length > 0) {
      setError(
        `Veuillez remplir tous les champs obligatoires: ${missingFields.join(
          ", "
        )}`
      );
      setIsLoading(false);
      return;
    }

    // const data = new FormData();
    // for (const key in formData) {
    //   if (key === "images") {
    //     formData.images.forEach((image) => data.append("images[]", image));
    //   } else if (key === "thumbnail") {
    //     data.append("thumbnail", formData.thumbnail);
    //   } else if (key === "itinerary") {
    //     const itineraryArray = formData.itinerary
    //       .split(",")
    //       .map((item, index) => {
    //         const [dayStr, ...descParts] = item.split(":");
    //         return {
    //           day: index + 1,
    //           description: descParts.join(":").trim() || dayStr.trim(),
    //         };
    //       });

    //     data.append("itinerary", JSON.stringify(itineraryArray));
    //   } else if (["duration_days", "price", "rating"].includes(key)) {
    //     // Convertir les champs numériques
    //     data.append(key, Number(formData[key]));
    //   } else {
    //     data.append(key, formData[key]);
    //   }
    // }
    // console.log("📤 Données envoyées à l'API:", {
    //   editingTourId,
    //   formData,
    //   formDataEntries: Array.from(data.entries()),
    // });

    // ✅ Préparez les données correctement
    const tourData = {
      title: formData.title,
      city: formData.city,
      duration_days: Number(formData.duration_days),
      start_date: formData.start_date || null,
      price: formData.price,
      rating: Number(formData.rating),
      summary: formData.summary,
    };

    // ✅ Ajoutez thumbnail seulement si c'est un nouveau fichier
    if (formData.thumbnail instanceof File) {
      tourData.thumbnail = formData.thumbnail;
    }

    // ✅ Ajoutez images seulement si nouvelles (filtrer les File objects)
    if (Array.isArray(formData.images)) {
      const newImages = formData.images.filter((img) => img instanceof File);
      if (newImages.length > 0) {
        tourData.images = newImages;
      }
    }

    // ✅ Envoyer les images à supprimer
    if (imagesToDelete.length > 0) {
      tourData.delete_images = imagesToDelete;
    }

    // ✅ Ajoutez itinerary seulement si modifié (format JSON array)
    if (Array.isArray(formData.itinerary) && formData.itinerary.length > 0) {
      tourData.itinerary = formData.itinerary;
    }

    // Send tour data to API

    try {
      const { data: updatedTour } = await tourApi.updateTour(
        editingTourId,
        tourData
      );
      setTours((prev) =>
        prev.map((tour) => (tour.id === editingTourId ? updatedTour : tour))
      );
      setSuccess("Circuit mis à jour avec succès !");
    } catch (err) {
      // Show detailed validation errors from the backend
      const errData = err.response?.data;
      if (errData?.errors) {
        const messages = Object.values(errData.errors).flat().join(', ');
        setError(messages);
      } else {
        setError(
          errData?.message || err.message || "Échec de la mise à jour du circuit."
        );
      }
    } finally {
      setIsLoading(false);
    }
    setEditingTourId(null);
    setFormData({
      title: "",
      city: "",
      duration_days: "",
      start_date: "",
      price: "",
      rating: "",
      thumbnail: null,
      images: [],
      summary: "",
      itinerary: [], // ✅ Array vide
    });
    setThumbnailPreview(null);
    setImagePreviews([]);
    setImagesToDelete([]); // ✅ Réinitialiser la liste des images à supprimer
    setShowForm(false); // Hide the form after updating
  };

  const cancelEditing = () => {
    setEditingTourId(null);
    setFormData({
      title: "",
      city: "",
      duration_days: "",
      start_date: "",
      price: "",
      rating: "",
      thumbnail: null,
      images: [],
      summary: "",
      itinerary: [], // ✅ Array vide
    });
    setThumbnailPreview(null);
    setImagePreviews([]);
    setImagesToDelete([]); // ✅ Réinitialiser la liste des images à supprimer
    setShowForm(false); // Hide the form after canceling
  };

  return (
    <div className="space-y-4">
      {error && <AlertMessage type="error">{error}</AlertMessage>}
      {success && <AlertMessage type="success">{success}</AlertMessage>}
      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={() => {
            setShowForm(true);
            setEditingTourId(null);
            setFormData({
              title: "",
              city: "",
              duration_days: "",
              start_date: "",
              price: "",
              rating: "",
              thumbnail: null,
              images: [],
              summary: "",
              itinerary: [], // ✅ Array vide
            });
            setThumbnailPreview(null);
            setImagePreviews([]);
            setImagesToDelete([]); // ✅ Réinitialiser la liste des images à supprimer
          }}
          className="rounded-md bg-emerald-600 px-6 py-2 text-sm text-white hover:bg-emerald-700"
        >
          Add New Tour
        </button>
      </div>
      {showForm && (
        <TourForm
          formData={formData}
          setFormData={setFormData}
          thumbnailPreview={thumbnailPreview}
          setThumbnailPreview={setThumbnailPreview}
          imagePreviews={imagePreviews}
          setImagePreviews={setImagePreviews}
          handleInputChange={handleInputChange}
          handleThumbnailChange={handleThumbnailChange}
          handleImagesChange={handleImagesChange}
          removeImage={removeImage}
          handleSubmit={editingTourId ? handleUpdate : add}
          editingTourId={editingTourId}
          isLoading={isLoading}
          cancelEditing={cancelEditing}
        />
      )}

      <TourList tours={tours} startEditing={startEditing} remove={remove} />
    </div>
  );
}

function AdminBookingsComponent() {
  const [bookings, setBookings] = useState([]);
  const [tours, setTours] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(null); // booking ID or booking object
  const [showDeleteModal, setShowDeleteModal] = useState(null); // booking to delete

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Manual booking form state
  const [manualForm, setManualForm] = useState({
    tour_id: "",
    customer_name: "",
    email: "",
    phone: "",
    number_of_people: 1,
    booking_date: getTodayDate(),
    amount_paid: 0,
    payment_method: "cash",
    admin_notes: "",
  });

  const [phoneError, setPhoneError] = useState("");
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsSearch, setBookingsSearch] = useState("");
  const [bookingsStatusFilter, setBookingsStatusFilter] = useState("all");
  const [bookingsSort, setBookingsSort] = useState("newest");
  const [showNotesModal, setShowNotesModal] = useState(null);
  const [notesInput, setNotesInput] = useState("");
  const [bookingsView, setBookingsView] = useState("list"); // "list" or "calendar"
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [statusModal, setStatusModal] = useState(null); // { id, currentStatus, newStatus, customerName }
  const BOOKINGS_PER_PAGE = 10;

  // Payment recording form state
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    reference: "",
    notes: "",
  });

  // Get available tours (future tours only)
  const availableTours = tours.filter((tour) => {
    if (!tour.start_date) return true; // Show tours without start date
    const tourDate = new Date(tour.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return tourDate >= today;
  });

  // Get remaining amount for a booking
  const getRemainingAmount = (booking) => {
    return parseFloat(booking.remaining_amount || booking.total_price - (booking.amount_paid || 0));
  };

  useEffect(() => {
    loadBookings();
    loadTours();
  }, []);

  const loadBookings = async () => {
    setError("");
    setIsLoading(true);
    try {
      const { data } = await bookingApi.listBookings();
      setBookings(data);
      setBookingsPage(1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load bookings.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadTours = async () => {
    try {
      const { data } = await tourApi.listTours();
      setTours(data);
    } catch (err) {
      // Error handled by setError
    }
  };

  const handleStatusChange = (id, newStatus) => {
    const booking = bookings.find((b) => b.id === id);
    if (!booking || booking.status === newStatus) return;
    setStatusModal({ id, currentStatus: booking.status, newStatus, customerName: booking.customer_name || booking.user?.name || "Unknown" });
  };

  const confirmStatusChange = async () => {
    if (!statusModal) return;
    const { id, newStatus } = statusModal;
    setStatusModal(null);
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const { data } = await bookingApi.updateBooking(id, { status: newStatus });
      const updatedBooking = data.booking || data;
      setBookings((prev) => prev.map((b) => (b.id === id ? updatedBooking : b)));
      setSuccess("Booking status updated!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      await bookingApi.deleteBooking(id);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSuccess("Booking deleted successfully!");
      setShowDeleteModal(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete booking.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotes = async (bookingId) => {
    setError("");
    setIsLoading(true);
    try {
      const { data } = await bookingApi.updateBooking(bookingId, { admin_notes: notesInput });
      const updatedBooking = data.booking || data;
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? updatedBooking : b)));
      setSuccess("Notes updated!");
      setShowNotesModal(null);
      setNotesInput("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update notes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualBookingSubmit = async (e) => {
    e.preventDefault();
    if (manualForm.phone && !/^[\d\s+\-()]{8,25}$/.test(manualForm.phone)) {
      setPhoneError("Invalid phone format (digits, spaces, +, -, parentheses, 8-25 chars)");
      return;
    }
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const { data } = await bookingApi.createManualBooking(manualForm);
      // Add booking to the list without full refresh
      const newBooking = data.booking;
      if (newBooking) {
        setBookings((prev) => [newBooking, ...prev]);
      }
      setSuccess("Manual booking created successfully!");
      setShowManualForm(false);
      // Reset form with today's date
      setManualForm({
        tour_id: "",
        customer_name: "",
        email: "",
        phone: "",
        number_of_people: 1,
        booking_date: getTodayDate(),
        amount_paid: 0,
        payment_method: "cash",
        admin_notes: "",
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create booking.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordPayment = async (booking) => {
    const amount = parseFloat(paymentForm.amount);
    const remaining = getRemainingAmount(booking);

    // Extra safety check (button should already be disabled)
    if (!paymentForm.amount || amount <= 0 || amount > remaining) return;

    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const { data } = await bookingApi.recordPayment(booking.id, {
        amount: amount,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        notes: paymentForm.notes,
      });

      // Update the booking in the list
      setBookings((prev) =>
        prev.map((b) => (b.id === booking.id ? data.booking : b))
      );

      setSuccess("Payment recorded successfully!");
      setShowPaymentModal(null);
      setPaymentForm({ amount: "", payment_method: "cash", reference: "", notes: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to record payment.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    };
    return colors[status] || colors.pending;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      deposit_paid: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      awaiting_call: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    };
    return colors[status] || colors.pending;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      deposit_paid: "Deposit Paid",
      paid: "Fully Paid",
      awaiting_call: "Awaiting Call",
      failed: "Failed",
    };
    return labels[status] || status;
  };

  if (isLoading && bookings.length === 0) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 w-36 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
        <div className="flex gap-3">
          <div className="h-7 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
          <div className="h-9 w-48 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
      <div className="h-10 w-full animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
      {[1,2,3].map(i => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-5 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                <div className="h-5 w-12 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="h-6 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="h-4 w-48 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            <div className="grid grid-cols-5 gap-3">
              {[1,2,3,4,5].map(j => (
                <div key={j} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
              ))}
            </div>
            <div className="h-16 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
          </div>
        </div>
      ))}
    </div>
  );

  const filteredBookings = bookings
    .filter((b) => {
      if (bookingsStatusFilter !== "all" && b.status !== bookingsStatusFilter) return false;
      if (bookingsSearch.trim()) {
        const q = bookingsSearch.toLowerCase();
        return (
          (b.customer_name || b.user?.name || "").toLowerCase().includes(q) ||
          (b.email || "").toLowerCase().includes(q) ||
          (b.tour?.title || "").toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (bookingsSort) {
        case "newest": return new Date(b.created_at) - new Date(a.created_at);
        case "oldest": return new Date(a.created_at) - new Date(b.created_at);
        case "amount_high": return (b.total_price || 0) - (a.total_price || 0);
        case "amount_low": return (a.total_price || 0) - (b.total_price || 0);
        case "name_az": return (a.customer_name || a.user?.name || "").localeCompare(b.customer_name || b.user?.name || "");
        case "name_za": return (b.customer_name || b.user?.name || "").localeCompare(a.customer_name || a.user?.name || "");
        default: return 0;
      }
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Bookings</h1>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            {bookings.length} {bookings.length === 1 ? "Booking" : "Bookings"}
          </span>
          <button
            onClick={() => setShowManualForm(true)}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition flex items-center gap-2"
          >
            <span>+</span> Create Manual Booking
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setBookingsView("list")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            bookingsView === "list"
              ? "bg-emerald-600 text-white"
              : "border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
          List
        </button>
        <button
          onClick={() => setBookingsView("calendar")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            bookingsView === "calendar"
              ? "bg-emerald-600 text-white"
              : "border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          Calendar
        </button>
      </div>

      {/* Search, Filter & Sort (only in list view) */}
      {bookingsView === "list" && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by customer name, email, or tour..."
            value={bookingsSearch}
            onChange={(e) => { setBookingsSearch(e.target.value); setBookingsPage(1); }}
            className="w-full rounded-lg border border-slate-300 pl-10 pr-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          <svg className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <select
          value={bookingsStatusFilter}
          onChange={(e) => { setBookingsStatusFilter(e.target.value); setBookingsPage(1); }}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </select>
        <select
          value={bookingsSort}
          onChange={(e) => setBookingsSort(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="amount_high">Amount: High to Low</option>
          <option value="amount_low">Amount: Low to High</option>
          <option value="name_az">Name: A-Z</option>
          <option value="name_za">Name: Z-A</option>
        </select>
      </div>
      )}

      {error && <AlertMessage type="error">{error}</AlertMessage>}
      {success && <AlertMessage type="success">{success}</AlertMessage>}

      {/* Manual Booking Form Modal */}
      {showManualForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Create Manual Booking</h2>
              <button
                onClick={() => setShowManualForm(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                X
              </button>
            </div>

            <form onSubmit={handleManualBookingSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tour *</label>
                <select
                  value={manualForm.tour_id}
                  onChange={(e) => {
                    const selectedTour = availableTours.find(t => String(t.id) === e.target.value);
                    setManualForm({
                      ...manualForm,
                      tour_id: e.target.value,
                      booking_date: selectedTour?.start_date || getTodayDate(),
                    });
                  }}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                >
                  <option value="">Select a tour</option>
                  {availableTours.map((tour) => (
                    <option key={tour.id} value={tour.id}>
                      {tour.title} - ${tour.price}{tour.start_date ? ` - ${new Date(tour.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={manualForm.customer_name}
                    onChange={(e) => setManualForm({ ...manualForm, customer_name: e.target.value })}
                    required
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={manualForm.phone}
                    onChange={(e) => {
                      const val = e.target.value;
                      setManualForm({ ...manualForm, phone: val });
                      if (val && !/^[\d\s+\-()]{8,25}$/.test(val)) {
                        setPhoneError("Invalid phone format (digits, spaces, +, -, parentheses, 8-25 chars)");
                      } else {
                        setPhoneError("");
                      }
                    }}
                    required
                    className={`w-full rounded-lg border px-3 py-2 dark:border-slate-700 dark:bg-slate-800 ${phoneError ? "border-red-500" : "border-slate-300"}`}
                  />
                  {phoneError && <p className="mt-1 text-xs text-red-500">{phoneError}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={manualForm.email}
                  onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Travelers *</label>
                  <input
                    type="number"
                    min="1"
                    value={manualForm.number_of_people}
                    onChange={(e) => setManualForm({ ...manualForm, number_of_people: parseInt(e.target.value) })}
                    onFocus={(e) => e.target.select()}
                    required
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Booking Date *</label>
                  {(() => {
                    const selectedTour = availableTours.find(t => String(t.id) === manualForm.tour_id);
                    const hasStartDate = selectedTour?.start_date;
                    return (
                      <>
                        <input
                          type="date"
                          value={manualForm.booking_date}
                          onChange={(e) => !hasStartDate && setManualForm({ ...manualForm, booking_date: e.target.value })}
                          readOnly={!!hasStartDate}
                          required
                          className={`w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800 ${hasStartDate ? "bg-slate-100 text-slate-500 cursor-not-allowed dark:bg-slate-700" : ""}`}
                        />
                        {hasStartDate && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Date set from tour start date</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Amount Paid ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualForm.amount_paid}
                    onChange={(e) => setManualForm({ ...manualForm, amount_paid: parseFloat(e.target.value) || 0 })}
                    onFocus={(e) => e.target.select()}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Payment Method</label>
                  <select
                    value={manualForm.payment_method}
                    onChange={(e) => setManualForm({ ...manualForm, payment_method: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Admin Notes</label>
                <textarea
                  value={manualForm.admin_notes}
                  onChange={(e) => setManualForm({ ...manualForm, admin_notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  placeholder="Any internal notes about this booking..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowManualForm(false)}
                  className="flex-1 rounded-lg border border-slate-300 px-4 py-2 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isLoading ? "Creating..." : "Create Booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Recording Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Record Payment</h2>
              <button
                onClick={() => setShowPaymentModal(null)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                X
              </button>
            </div>

            {/* Booking Summary */}
            <div className="mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium">Booking:</span> {showPaymentModal.tour?.title || "N/A"} - {showPaymentModal.customer_name}
              </p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-slate-500 dark:text-slate-400">Total: <strong className="text-slate-900 dark:text-white">${showPaymentModal.total_price}</strong></span>
                <span className="text-emerald-600 dark:text-emerald-400">Paid: <strong>${parseFloat(showPaymentModal.amount_paid || 0).toFixed(2)}</strong></span>
                <span className="text-amber-600 dark:text-amber-400">Remaining: <strong>${getRemainingAmount(showPaymentModal).toFixed(2)}</strong></span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Amount ($) * <span className="text-slate-400 dark:text-slate-500 font-normal">(Max: ${getRemainingAmount(showPaymentModal).toFixed(2)})</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0.01"
                    max={getRemainingAmount(showPaymentModal)}
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    className={`flex-1 rounded-lg border px-3 py-2 dark:bg-slate-800 ${
                      paymentForm.amount && parseFloat(paymentForm.amount) > getRemainingAmount(showPaymentModal)
                        ? 'border-red-400 dark:border-red-500 ring-1 ring-red-400 dark:ring-red-500'
                        : 'border-slate-300 dark:border-slate-700'
                    }`}
                    placeholder={`Enter amount (max $${getRemainingAmount(showPaymentModal).toFixed(2)})`}
                  />
                  <button
                    type="button"
                    onClick={() => setPaymentForm({ ...paymentForm, amount: getRemainingAmount(showPaymentModal).toFixed(2) })}
                    className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 transition-colors whitespace-nowrap"
                    title="Fill with remaining amount"
                  >
                    Fill Max
                  </button>
                </div>
                {paymentForm.amount && parseFloat(paymentForm.amount) > getRemainingAmount(showPaymentModal) && (
                  <p className="mt-1.5 text-sm text-red-500 dark:text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Maximum allowed is ${getRemainingAmount(showPaymentModal).toFixed(2)}
                  </p>
                )}
                {paymentForm.amount && parseFloat(paymentForm.amount) <= 0 && (
                  <p className="mt-1.5 text-sm text-red-500 dark:text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    Amount must be greater than 0
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="stripe">Stripe</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Reference (optional)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  placeholder="Transaction ID or reference"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes (optional)</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                  placeholder="Any notes about this payment..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(null)}
                  className="flex-1 rounded-lg border border-slate-300 px-4 py-2 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRecordPayment(showPaymentModal)}
                  disabled={isLoading || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0 || parseFloat(paymentForm.amount) > getRemainingAmount(showPaymentModal)}
                  className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Recording..." : "Record Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {statusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 transform transition-all">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <svg className="h-7 w-7 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-center text-slate-900 dark:text-white mb-2">
              Change Status
            </h3>
            <p className="text-center text-sm text-slate-600 dark:text-slate-400 mb-4">
              Update <span className="font-semibold text-slate-800 dark:text-slate-200">{statusModal.customerName}</span>'s booking?
            </p>
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                { pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                  confirmed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
                  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
                  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
                }[statusModal.currentStatus] || "bg-slate-100 text-slate-700"
              }`}>
                {statusModal.currentStatus}
              </span>
              <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                { pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                  confirmed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
                  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
                  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
                }[statusModal.newStatus] || "bg-slate-100 text-slate-700"
              }`}>
                {statusModal.newStatus}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setStatusModal(null)}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 transform transition-all">
            {/* Warning Icon */}
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-center text-slate-900 dark:text-white mb-2">
              Delete Booking?
            </h3>

            {/* Message */}
            <p className="text-center text-slate-600 dark:text-slate-400 mb-4">
              Are you sure you want to delete this booking? This action cannot be undone.
            </p>

            {/* Booking Details */}
            <div className="mb-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Tour:</span>
                  <span className="font-medium text-slate-900 dark:text-white">{showDeleteModal.tour?.title || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Customer:</span>
                  <span className="font-medium text-slate-900 dark:text-white">{showDeleteModal.customer_name || showDeleteModal.user?.name || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Date:</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {new Date(showDeleteModal.booking_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Total:</span>
                  <span className="font-bold text-slate-900 dark:text-white">${showDeleteModal.total_price}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-3 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal.id)}
                disabled={isLoading}
                className="flex-1 rounded-lg bg-red-600 px-4 py-3 font-medium text-white hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Booking
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Admin Notes - Booking #{showNotesModal.id}
              </h2>
              <button
                onClick={() => { setShowNotesModal(null); setNotesInput(""); }}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
              >
                X
              </button>
            </div>
            <textarea
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              placeholder="Add notes about this booking..."
              maxLength={1000}
              rows={5}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <p className="mt-1 text-xs text-slate-400 text-right">{notesInput.length}/1000</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setShowNotesModal(null); setNotesInput(""); }}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveNotes(showNotesModal.id)}
                disabled={isLoading}
                className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {isLoading ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {bookingsView === "calendar" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              &lt; Prev
            </button>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </h3>
            <button
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              Next &gt;
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-px mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 py-2">{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
            {(() => {
              const year = calendarMonth.getFullYear();
              const month = calendarMonth.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              // Monday = 0, Sunday = 6
              let startDay = firstDay.getDay() - 1;
              if (startDay < 0) startDay = 6;
              const totalDays = lastDay.getDate();
              const cells = [];

              // Group bookings by date
              const bookingsByDate = {};
              bookings.forEach((b) => {
                const raw = b.booking_date || b.created_at;
                const d = raw ? raw.split("T")[0] : null;
                if (d) {
                  if (!bookingsByDate[d]) bookingsByDate[d] = [];
                  bookingsByDate[d].push(b);
                }
              });

              // Empty cells before first day
              for (let i = 0; i < startDay; i++) {
                cells.push(
                  <div key={`empty-${i}`} className="bg-slate-50 dark:bg-slate-800/50 min-h-[80px] p-1" />
                );
              }

              const today = new Date().toISOString().split("T")[0];

              for (let day = 1; day <= totalDays; day++) {
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const dayBookings = bookingsByDate[dateStr] || [];
                const isToday = dateStr === today;

                cells.push(
                  <div
                    key={day}
                    onClick={() => dayBookings.length > 0 && setSelectedCalendarDate(selectedCalendarDate === dateStr ? null : dateStr)}
                    className={`bg-white dark:bg-slate-900 min-h-[80px] p-1 transition-colors ${isToday ? "ring-2 ring-inset ring-emerald-500" : ""} ${dayBookings.length > 0 ? "cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/20" : ""} ${selectedCalendarDate === dateStr ? "bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-inset ring-emerald-400" : ""}`}
                  >
                    <div className={`text-xs font-medium mb-1 ${isToday ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-slate-700 dark:text-slate-300"}`}>
                      {day}
                    </div>
                    <div className="space-y-0.5">
                      {dayBookings.slice(0, 3).map((b) => {
                        const statusColor = {
                          pending: "bg-yellow-200 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                          confirmed: "bg-green-200 text-green-800 dark:bg-green-900/40 dark:text-green-300",
                          cancelled: "bg-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-300",
                          completed: "bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
                        };
                        return (
                          <div
                            key={b.id}
                            className={`text-[10px] leading-tight rounded px-1 py-0.5 truncate ${statusColor[b.status] || "bg-slate-200 text-slate-700"}`}
                            title={`${b.customer_name || b.user?.name || "?"} - ${b.tour?.title || "?"} (${b.status})`}
                          >
                            {b.customer_name || b.user?.name || "?"}
                          </div>
                        );
                      })}
                      {dayBookings.length > 3 && (
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 pl-1">
                          +{dayBookings.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return cells;
            })()}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            {[
              { label: "Pending", color: "bg-yellow-300" },
              { label: "Confirmed", color: "bg-green-300" },
              { label: "Completed", color: "bg-blue-300" },
              { label: "Cancelled", color: "bg-red-300" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <div className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                {s.label}
              </div>
            ))}
            <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto">Click a day to see details</span>
          </div>

          {/* Selected Day Detail Panel */}
          {selectedCalendarDate && (() => {
            const dayBookingsList = bookings.filter((b) => {
              const raw = b.booking_date || b.created_at;
              const d = raw ? raw.split("T")[0] : null;
              return d === selectedCalendarDate;
            });
            if (dayBookingsList.length === 0) return null;
            const dateLabel = new Date(selectedCalendarDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
            const statusBadge = {
              pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
              confirmed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
              cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
              completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
            };
            return (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-800 dark:bg-emerald-900/10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {dateLabel} — {dayBookingsList.length} booking{dayBookingsList.length > 1 ? "s" : ""}
                  </h4>
                  <button
                    onClick={() => setSelectedCalendarDate(null)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg leading-none"
                  >
                    &times;
                  </button>
                </div>
                <div className="space-y-2">
                  {dayBookingsList.map((b) => (
                    <div key={b.id} className="flex items-center justify-between rounded-lg bg-white dark:bg-slate-800 p-3 text-sm border border-slate-200 dark:border-slate-700">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                          {b.customer_name || b.user?.name || "Unknown"}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {b.tour?.title || "No tour"} — {b.number_of_people} traveler{b.number_of_people > 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {b.total_price}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusBadge[b.status] || "bg-slate-100 text-slate-700"}`}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* List View */}
      {bookingsView === "list" && (filteredBookings.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
            <span className="text-3xl">📅</span>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {bookingsSearch.trim() || bookingsStatusFilter !== "all" ? "No bookings match your filters" : "No bookings yet"}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {bookingsSearch.trim() || bookingsStatusFilter !== "all" ? "Try different search or filter options" : "Tour bookings will appear here"}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredBookings.slice((bookingsPage - 1) * BOOKINGS_PER_PAGE, bookingsPage * BOOKINGS_PER_PAGE).map((b) => (
            <div
              key={b.id}
              className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                {/* Left Side - Booking Info */}
                <div className="flex-1 space-y-3">
                  {/* Header with Tour and User */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          {b.tour?.title || "N/A"}
                        </h3>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          #{b.id}
                        </span>
                        {b.created_by && (
                          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            Manual
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Customer:</span>
                          <span className="text-slate-900 dark:text-slate-100 font-medium">
                            {b.customer_name || b.user?.name || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(b.status)}`}>
                        {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                      </span>
                      {b.payment_status && (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getPaymentStatusColor(b.payment_status)}`}>
                          {getPaymentStatusLabel(b.payment_status)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Booking Details Grid */}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Date</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {new Date(b.booking_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Travelers</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{b.number_of_people}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
                      <p className="font-bold text-slate-900 dark:text-slate-100">${b.total_price}</p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 px-3 py-2 dark:bg-emerald-900/20">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Paid</p>
                      <p className="font-bold text-emerald-600 dark:text-emerald-400">
                        ${parseFloat(b.amount_paid || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Remaining</p>
                      <p className="font-bold text-amber-600 dark:text-amber-400">
                        ${parseFloat(b.remaining_amount || b.total_price - (b.amount_paid || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Contact Information
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {b.email && (
                        <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-sm text-emerald-600 hover:underline dark:text-emerald-400">
                          <span>📧</span><span>{b.email}</span>
                        </a>
                      )}
                      {b.phone && (
                        <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-sm text-emerald-600 hover:underline dark:text-emerald-400">
                          <span>📱</span><span>{b.phone}</span>
                        </a>
                      )}
                      {b.phone && (
                        <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-green-600 hover:underline dark:text-green-400">
                          <span>💬</span><span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                    {b.admin_notes && (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 italic">
                        Note: {b.admin_notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Side - Actions */}
                <div className="flex flex-row gap-2 lg:flex-col lg:items-end">
                  <select
                    value={b.status}
                    onChange={(e) => handleStatusChange(b.id, e.target.value)}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:border-emerald-500 dark:border-slate-700 dark:bg-slate-800 lg:w-auto"
                    disabled={isLoading}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                  </select>
                  <button
                    onClick={() => setShowPaymentModal(b)}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition"
                    title="Record payment"
                  >
                    + Payment
                  </button>
                  <button
                    onClick={() => { setShowNotesModal(b); setNotesInput(b.admin_notes || ""); }}
                    className="rounded-lg bg-slate-600 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 transition"
                    title="Edit notes"
                  >
                    Notes
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(b)}
                    disabled={isLoading}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-red-700 disabled:opacity-50"
                    title="Delete booking"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Bookings Pagination */}
      {bookingsView === "list" && filteredBookings.length > BOOKINGS_PER_PAGE && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setBookingsPage((p) => Math.max(1, p - 1))}
            disabled={bookingsPage === 1}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Previous
          </button>
          {Array.from({ length: Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setBookingsPage(i + 1)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                bookingsPage === i + 1
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setBookingsPage((p) => Math.min(Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE), p + 1))}
            disabled={bookingsPage === Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [heroImages, setHeroImages] = useState([]);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroDeleteTarget, setHeroDeleteTarget] = useState(null); // image URL to confirm delete
  const [heroDeleting, setHeroDeleting] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await settingsApi.getAllSettings();
      const settingsMap = {};
      (response.data || []).forEach((s) => {
        settingsMap[s.key] = s.value;
      });
      setSettings(settingsMap);
      setOriginalSettings({ ...settingsMap });
      // Parse hero_images (may be array already or JSON string)
      const rawImgs = settingsMap.hero_images;
      if (Array.isArray(rawImgs)) {
        setHeroImages(rawImgs);
      } else if (typeof rawImgs === 'string' && rawImgs) {
        try { setHeroImages(JSON.parse(rawImgs)); } catch { setHeroImages([]); }
      } else {
        setHeroImages([]);
      }
    } catch (err) {
      setError("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  // Compute display images: custom ones if any, otherwise defaults
  const displayHeroImages = heroImages.length > 0 ? heroImages : defaultHeroImages;
  const isUsingDefaults = heroImages.length === 0;

  const handleHeroUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setHeroUploading(true);
    setError("");
    try {
      // If currently using defaults, first save defaults to backend, then upload new ones on top
      if (isUsingDefaults) {
        await settingsApi.updateSetting("hero_images", JSON.stringify(defaultHeroImages));
      }
      const response = await settingsApi.uploadHeroImages(files);
      const allImages = response.data?.all_images || response.data;
      setHeroImages(Array.isArray(allImages) ? allImages : []);
      setSuccess("Hero images uploaded successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload hero images");
    } finally {
      setHeroUploading(false);
      e.target.value = "";
    }
  };

  const handleHeroDeleteConfirm = async () => {
    if (!heroDeleteTarget) return;
    setHeroDeleting(true);
    setError("");
    try {
      if (isUsingDefaults) {
        const remaining = defaultHeroImages.filter((img) => img !== heroDeleteTarget);
        await settingsApi.updateSetting("hero_images", JSON.stringify(remaining));
        setHeroImages(remaining);
      } else {
        const response = await settingsApi.deleteHeroImage(heroDeleteTarget);
        const allImages = response.data?.all_images || response.data;
        setHeroImages(Array.isArray(allImages) ? allImages : []);
      }
      setSuccess("Hero image removed!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove hero image");
    } finally {
      setHeroDeleting(false);
      setHeroDeleteTarget(null);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const handleSave = async () => {
    // Validate
    const deposit = Number(settings.deposit_percentage);
    if (deposit < 1 || deposit > 100) {
      setError("Deposit percentage must be between 1 and 100");
      return;
    }
    const maxTravelers = Number(settings.max_travelers_per_booking);
    if (maxTravelers < 1 || maxTravelers > 100) {
      setError("Max travelers must be between 1 and 100");
      return;
    }
    const discount = Number(settings.discount_percentage);
    if (discount < 0 || discount > 90) {
      setError("Discount percentage must be between 0 and 90");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccess("");

    try {
      // Save only changed settings
      const changedKeys = Object.keys(settings).filter(
        (key) => String(settings[key]) !== String(originalSettings[key])
      );

      for (const key of changedKeys) {
        await settingsApi.updateSetting(key, settings[key]);
      }

      setOriginalSettings({ ...settings });
      setSuccess("Settings saved successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Settings</h1>
        {hasChanges && (
          <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-800">
            Unsaved changes
          </span>
        )}
      </div>

      {error && <AlertMessage type="error">{error}</AlertMessage>}
      {success && <AlertMessage type="success">{success}</AlertMessage>}

      {/* Company Information */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🏢</span>
          Company Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Company Name
            </label>
            <input
              type="text"
              value={settings.company_name || ""}
              onChange={(e) => updateSetting("company_name", e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Company Email
            </label>
            <input
              type="email"
              value={settings.company_email || ""}
              onChange={(e) => updateSetting("company_email", e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              WhatsApp Number
            </label>
            <input
              type="text"
              value={settings.company_whatsapp || ""}
              onChange={(e) => updateSetting("company_whatsapp", e.target.value)}
              placeholder="+212..."
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Phone Number
            </label>
            <input
              type="text"
              value={settings.company_phone || ""}
              onChange={(e) => updateSetting("company_phone", e.target.value)}
              placeholder="+212..."
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Address
            </label>
            <input
              type="text"
              value={settings.company_address || ""}
              onChange={(e) => updateSetting("company_address", e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Payment Settings */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">💳</span>
          Payment Settings
        </h2>
        <div className="space-y-6">
          {/* Payment Enabled Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Online Payment Required
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {settings.payment_enabled === 'true' || settings.payment_enabled === true
                  ? "Customers must pay a deposit online (Stripe/PayPal) to complete their booking."
                  : "Customers can book without paying online. You will receive their details and contact them to arrange payment."}
              </p>
            </div>
            <button
              onClick={() => updateSetting("payment_enabled", settings.payment_enabled === 'true' || settings.payment_enabled === true ? 'false' : 'true')}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                settings.payment_enabled === 'true' || settings.payment_enabled === true
                  ? "bg-emerald-600"
                  : "bg-slate-300 dark:bg-slate-600"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                  settings.payment_enabled === 'true' || settings.payment_enabled === true ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Deposit Percentage
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Percentage of the total tour price that customers pay upfront when booking online.
            </p>
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.deposit_percentage || 20}
                  onChange={(e) => updateSetting("deposit_percentage", Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  className="w-24 rounded-lg border border-slate-300 px-4 py-2 pr-8 text-lg font-semibold text-center dark:border-slate-700 dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                <p>Example: For a <strong>$1,000</strong> tour</p>
                <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                  Deposit: ${(1000 * (settings.deposit_percentage || 20) / 100).toFixed(0)}
                </p>
                <p className="text-amber-600 dark:text-amber-400">
                  Remaining: ${(1000 - 1000 * (settings.deposit_percentage || 20) / 100).toFixed(0)}
                </p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Currency Symbol
            </label>
            <input
              type="text"
              value={settings.currency_symbol || "$"}
              onChange={(e) => updateSetting("currency_symbol", e.target.value)}
              maxLength={5}
              className="w-24 rounded-lg border border-slate-300 px-4 py-2.5 text-center text-lg font-semibold dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Booking Settings */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">📅</span>
          Booking Settings
        </h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Max Travelers Per Booking
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Maximum number of travelers a customer can book in a single booking.
          </p>
          <input
            type="number"
            min="1"
            max="100"
            value={settings.max_travelers_per_booking || 20}
            onChange={(e) => updateSetting("max_travelers_per_booking", Number(e.target.value))}
            onFocus={(e) => e.target.select()}
            className="w-24 rounded-lg border border-slate-300 px-4 py-2.5 text-center text-lg font-semibold dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>

      {/* Promotions */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🏷️</span>
          Promotions
        </h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Global Discount
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Apply a discount to all tour prices. Set to 0 to disable. Both original and discounted prices will be shown.
          </p>
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="number"
                min="0"
                max="90"
                value={settings.discount_percentage ?? 0}
                onChange={(e) => updateSetting("discount_percentage", Number(e.target.value))}
                onFocus={(e) => e.target.select()}
                className="w-24 rounded-lg border border-slate-300 px-4 py-2 pr-8 text-lg font-semibold text-center dark:border-slate-700 dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {Number(settings.discount_percentage) > 0 ? (
                <>
                  <p>Example: For a <strong>{settings.currency_symbol || "$"}100</strong> tour</p>
                  <p className="flex items-center gap-2">
                    <span className="text-slate-400 line-through">{settings.currency_symbol || "$"}100</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                      {settings.currency_symbol || "$"}{(100 - 100 * Number(settings.discount_percentage) / 100).toFixed(0)}
                    </span>
                    <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-semibold">
                      -{settings.discount_percentage}%
                    </span>
                  </p>
                </>
              ) : (
                <p className="text-slate-500">No discount active</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🔔</span>
          Notifications
        </h2>

        {/* Admin Notification Email */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Admin Notification Email
          </label>
          <input
            type="email"
            value={settings.admin_email || ""}
            onChange={(e) => updateSetting("admin_email", e.target.value)}
            placeholder="adamlarabi10@gmail.com"
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            This email receives all booking summaries (daily, weekly, monthly).
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Email Notifications
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Receive daily and weekly email summaries about bookings and revenue.
            </p>
          </div>
          <button
            onClick={() => updateSetting("email_notifications_enabled", !settings.email_notifications_enabled)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              settings.email_notifications_enabled
                ? "bg-emerald-600"
                : "bg-slate-300 dark:bg-slate-600"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                settings.email_notifications_enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Hero Images */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="text-xl">🖼️</span>
          Hero Section Images
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          Manage the homepage hero carousel images. Upload custom images or use the defaults. Hover over an image to delete it.
        </p>

        {/* Current Active Images */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-block h-2 w-2 rounded-full ${isUsingDefaults ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {isUsingDefaults
                ? `${defaultHeroImages.length} Default Images (Built-in)`
                : `${heroImages.length} Custom Image${heroImages.length !== 1 ? 's' : ''} (Active)`}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayHeroImages.map((img, i) => (
              <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                <img
                  src={img}
                  alt={`Hero ${i + 1}`}
                  className="w-full h-28 object-cover"
                />
                <button
                  onClick={() => setHeroDeleteTarget(img)}
                  className="absolute top-1.5 right-1.5 rounded-full bg-red-600 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                  title="Remove image"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                  #{i + 1}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upload Button */}
        <label className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 p-4 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors ${heroUploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleHeroUpload}
            className="hidden"
            disabled={heroUploading}
          />
          {heroUploading ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
              <span className="text-sm text-slate-600 dark:text-slate-400">Uploading...</span>
            </>
          ) : (
            <>
              <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Add more images
              </span>
            </>
          )}
        </label>
      </div>

      {/* Hero Image Delete Confirmation Modal */}
      {heroDeleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !heroDeleting && setHeroDeleteTarget(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
            {/* Preview */}
            <div className="mx-auto mb-4 w-full h-40 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
              <img
                src={heroDeleteTarget}
                alt="Image to delete"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Warning Icon + Text */}
            <div className="text-center mb-5">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Remove this image?
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                This image will be removed from the hero carousel.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setHeroDeleteTarget(null)}
                disabled={heroDeleting}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleHeroDeleteConfirm}
                disabled={heroDeleting}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {heroDeleting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Removing...
                  </>
                ) : (
                  "Remove"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="sticky bottom-4 z-10">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-800 dark:bg-slate-900 flex items-center justify-between">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {hasChanges ? "You have unsaved changes" : "All settings are up to date"}
          </p>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className={`rounded-lg px-6 py-2.5 text-sm font-semibold transition-all ${
              hasChanges
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-400"
            } disabled:opacity-50`}
          >
            {isSaving ? "Saving..." : hasChanges ? "Save All Changes" : "No Changes"}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-5">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2 flex items-center gap-2">
          <span>ℹ️</span>
          How Settings Work
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
          <li>Changes are saved together when you click "Save All Changes"</li>
          <li>Deposit percentage applies to all new online bookings via Stripe</li>
          <li>WhatsApp number is shown to customers for direct contact</li>
          <li>Email notifications control daily and weekly summary emails</li>
          <li>Manual bookings allow flexible deposit amounts regardless of this setting</li>
        </ul>
      </div>
    </div>
  );
}
